import PersonOptionLabel from 'components/employee/PersonOptionLabel';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Loader2Icon } from 'components/icons';
import { useMemo, useState } from 'react';
import { useGetAllEmployeesListWithFiltersQuery } from 'store/api/employees';
import { EmployeeListItemMinDto } from 'types/employees';
import { t } from 'utils/getTranslations';
import { useSameNameEmail } from 'utils/sameNameEmail';
import { FilterTriggerIndicator } from './FilterTriggerIndicator';
import { FILTER_TRIGGER_CLASSES } from './toolbarStyles';

const ALL_EMPLOYEES_PAGE_SIZE = 1000;

export function AuthorFilter({ selected, onChange }: { selected: number[]; onChange: (selected: number[]) => void }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const { data, isLoading } = useGetAllEmployeesListWithFiltersQuery(
        { page: 0, pageSize: ALL_EMPLOYEES_PAGE_SIZE },
        { skip: !open }
    );

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return (data?.content ?? [])
            .filter((employee) => !needle || employee.name.toLowerCase().includes(needle))
            .toSorted((a, b) => a.name.localeCompare(b.name));
    }, [data, query]);
    const getSameNameEmail = useSameNameEmail(visible);
    const allVisibleSelected = visible.every((employee) => selected.includes(employee.id));

    const selectAllVisible = () => {
        onChange([...new Set([...selected, ...visible.map((employee) => employee.id)])]);
    };

    const toggle = (employee: EmployeeListItemMinDto) => {
        onChange(
            selected.includes(employee.id) ? selected.filter((id) => id !== employee.id) : [...selected, employee.id]
        );
    };

    return (
        <Popover
            open={open}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (!nextOpen) {
                    setQuery('');
                }
            }}
        >
            <PopoverTrigger className={FILTER_TRIGGER_CLASSES}>
                {t('audit-trail.author')}
                <FilterTriggerIndicator count={selected.length} />
            </PopoverTrigger>
            <PopoverContent align="start" aria-label={t('audit-trail.author')} className="w-[280px] rounded-xl p-1.5">
                <Command shouldFilter={false}>
                    <CommandInput value={query} placeholder={t('audit-trail.author')} onValueChange={setQuery} />
                    <CommandList className="max-h-[300px] [scrollbar-gutter:stable]">
                        {isLoading && (
                            <div className="text-muted-foreground flex items-center justify-center py-3">
                                <Loader2Icon className="size-3.5 animate-spin" />
                            </div>
                        )}
                        {!isLoading && visible.length === 0 && (
                            <CommandEmpty>{t('audit-trail.no-results')}</CommandEmpty>
                        )}
                        <CommandGroup>
                            {visible.map((employee) => (
                                <CommandItem
                                    key={employee.id}
                                    value={String(employee.id)}
                                    onSelect={() => toggle(employee)}
                                >
                                    <Checkbox
                                        checked={selected.includes(employee.id)}
                                        tabIndex={-1}
                                        className="pointer-events-none"
                                    />
                                    <PersonOptionLabel
                                        option={employee}
                                        label={employee.name}
                                        sameNameEmail={getSameNameEmail(employee)}
                                    />
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                    <div className="border-grey-100 flex gap-1 border-t pt-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-grey-600 flex-1 text-[12.5px]"
                            disabled={visible.length === 0 || allVisibleSelected}
                            onClick={selectAllVisible}
                        >
                            {t('audit-trail.select-all')}
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-grey-600 flex-1 text-[12.5px]"
                            disabled={selected.length === 0}
                            onClick={() => onChange([])}
                        >
                            {t('audit-trail.clear-all')}
                        </Button>
                    </div>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
